import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDrive, Mail, Plug, Unplug } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import { formatRelative } from '@/lib/utils';

/* ---------------------------------------------------------------------------
   `GET /v1/integrations` מחזיר שורה לכל ספק *שחובר אי פעם*. ספק שאין
   לו שורה כלל אינו "שגיאה" — הוא פשוט לא חובר, ולכן ברירת המחדל
   בתצוגה היא DISCONNECTED.
   --------------------------------------------------------------------------- */

const integrationStatusSchema = z.enum(['CONNECTED', 'EXPIRED', 'ERROR', 'DISCONNECTED']);
type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

const integrationSchema = z.object({
  // מחרוזת ולא enum: ספק חדש בשרת (WHATSAPP) לא אמור להפיל את כל הרשימה.
  provider: z.string(),
  status: integrationStatusSchema,
  lastError: z.string().nullish(),
  lastErrorAt: z.string().nullish(),
  lastSyncedAt: z.string().nullish(),
  updatedAt: z.string(),
});
const integrationListSchema = z.array(integrationSchema);

const connectResponseSchema = z.object({ authUrl: z.string().url() });
const disconnectResponseSchema = z.object({ disconnected: z.number().int() });

type GoogleProvider = 'GMAIL' | 'DRIVE';

const PROVIDERS: Array<{
  provider: GoogleProvider;
  title: string;
  descriptionKey: 'integrations.driveDescription';
  icon: typeof Mail;
}> = [
  // Gmail כבר לא כאן: קליטת מיילים עברה להעברה אוטומטית (EmailForwardingCard,
  // ADR 0001) — בלי OAuth ובלי גישה לכל התיבה.
  {
    provider: 'DRIVE',
    title: 'Google Drive',
    descriptionKey: 'integrations.driveDescription',
    icon: HardDrive,
  },
];

const STATUS: Record<IntegrationStatus, { tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  CONNECTED: { tone: 'success' },
  EXPIRED: { tone: 'warning' },
  ERROR: { tone: 'danger' },
  DISCONNECTED: { tone: 'neutral' },
};

export function IntegrationsPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pendingDisconnect, setPendingDisconnect] = useState<GoogleProvider | null>(null);

  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: ({ signal }) => request('/integrations', { schema: integrationListSchema, signal }),
  });

  const connect = useMutation({
    mutationFn: () => request('/integrations/google/connect', { schema: connectResponseSchema }),
    onSuccess: ({ authUrl }) => {
      // אי אפשר פשוט לנווט ל-`/v1/integrations/google/connect`:
      // הנתיב דורש Bearer ומחזיר JSON. מביאים ממנו את כתובת ההסכמה
      // (שמגדירה גם את ה-state ואת ה-cookie) ורק אז יוצאים ל-Google.
      window.location.assign(authUrl);
    },
    onError: (err: unknown) => {
      toast.error(t('integrations.connectFailed'), err instanceof ApiError ? err.message : t('common:errors.generic'));
    },
  });

  const disconnect = useMutation({
    mutationFn: (provider: GoogleProvider) =>
      request(`/integrations/${provider}`, {
        method: 'DELETE',
        schema: disconnectResponseSchema,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setPendingDisconnect(null);
      toast.success(t('integrations.disconnected'));
    },
    onError: (err: unknown) => {
      toast.error(t('integrations.disconnectFailed'), err instanceof ApiError ? err.message : t('common:errors.generic'));
    },
  });

  const byProvider = new Map(integrations.data?.map((i) => [i.provider, i]));
  const pendingMeta = PROVIDERS.find((p) => p.provider === pendingDisconnect);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google</CardTitle>
        <CardDescription>{t('integrations.googleDescription')}</CardDescription>
      </CardHeader>

      <CardContent className="p-0">
        {integrations.isLoading ? (
          <div className="divide-y divide-border">
            {PROVIDERS.map((p) => (
              <div key={p.provider} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="size-9 rounded-(--radius-md)" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : integrations.isError ? (
          <ErrorState error={integrations.error} onRetry={() => void integrations.refetch()} />
        ) : (
          <ul className="divide-y divide-border">
            {PROVIDERS.map(({ provider, title, descriptionKey, icon: Icon }) => {
              const row = byProvider.get(provider);
              const status: IntegrationStatus = row?.status ?? 'DISCONNECTED';
              const meta = STATUS[status];
              const connected = status === 'CONNECTED' || status === 'EXPIRED' || status === 'ERROR';

              return (
                <li key={provider} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-surface-sunken text-fg-muted">
                    <Icon className="size-4" aria-hidden />
                  </div>

                  <div className="min-w-40 flex-1">
                    <p className="text-sm font-medium text-fg">{title}</p>
                    <p className="mt-0.5 text-xs text-fg-muted">{t(descriptionKey)}</p>
                    {row?.lastSyncedAt ? (
                      <p className="mt-1 text-2xs text-fg-subtle">
                        {t('integrations.lastSynced', { when: formatRelative(row.lastSyncedAt) })}
                      </p>
                    ) : null}
                    {status === 'ERROR' && row?.lastError ? (
                      <p className="mt-1 text-2xs text-danger">{row.lastError}</p>
                    ) : null}
                  </div>

                  <Badge tone={meta.tone}>{t(`integrations.status.${status}`)}</Badge>

                  {connected ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPendingDisconnect(provider)}
                    >
                      <Unplug aria-hidden />
                      {t('integrations.disconnect')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      loading={connect.isPending}
                      disabled={connect.isPending}
                      onClick={() => connect.mutate()}
                      data-track="integration.google.connect"
                    >
                      <Plug aria-hidden />
                      {t('integrations.connect')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* ניתוק שובר את קליטת המיילים של כל הטננט — לכן אישור מפורש
          ולא לחיצה אחת. */}
      <Dialog
        open={pendingDisconnect !== null}
        onOpenChange={(open) => !open && setPendingDisconnect(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('integrations.disconnectTitle', { name: pendingMeta?.title ?? t('integrations.theConnection') })}
            </DialogTitle>
            <DialogDescription>{t('integrations.affectsAll')}</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-fg">
              {pendingDisconnect === 'GMAIL' ? t('integrations.gmailWarning') : t('integrations.driveWarning')}
            </p>
            <p className="text-xs text-fg-muted">{t('integrations.reconnectNote')}</p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setPendingDisconnect(null)}
              disabled={disconnect.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={disconnect.isPending}
              disabled={disconnect.isPending}
              onClick={() => pendingDisconnect && disconnect.mutate(pendingDisconnect)}
            >
              {t('integrations.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
